import { useCallback, useEffect, useState } from 'react';
import { Coins, Camera, Clock } from 'lucide-react';
import { api, ErrorApi } from '../api';
import ListaEstandar from '../ui/ListaEstandar';
import EstadoVacio from '../ui/EstadoVacio';
import ChipEstado from '../ui/ChipEstado';
import Ayuda from '../ui/Ayuda';
import ModalConfirmar from '../ui/ModalConfirmar';
import { useAvisos } from '../lib/contextoAvisos';
import { useAuth } from '../lib/contextoAuth';
import { estadoDeLaRevision, cuandoEnPalabras } from '../ventaja/ultimaRevision';

// =====================================================================
// DINERO EN LA MESA · Fase 5.3 de DIRECTRIZ-VENTAJA
// =====================================================================
//
// LA PANTALLA EXISTE POR UN NÚMERO, Y ES EL CUARTO. «Se perdió por no
// reclamar» es el único de los cuatro que no se arregla mirándolo, y es
// el argumento entero de la fase: las empresas no pierden porque el
// proveedor falle, pierden porque nadie reclama. Por eso sale con el
// mismo tamaño que los demás y no escondido en una nota al pie.
//
// AQUÍ NO SE COBRA NADA, y la pantalla lo dice en su primera línea. Una
// nota de cargo solo procede si la cláusula está en el contrato, y su
// tratamiento fiscal lo decide un contador. Esto propone, con su
// evidencia.

const dinero = (n) => new Intl.NumberFormat('es-MX', {
  style: 'currency', currency: 'MXN',
}).format(Number(n || 0));

const TIPO = {
  ENTREGA_TARDIA: { etiqueta: 'Entregó tarde', color: 'ambar' },
  FALTANTE: { etiqueta: 'Faltó material', color: 'rojo' },
  RECHAZO_CALIDAD: { etiqueta: 'Rechazado por calidad', color: 'rojo' },
};

const ESTADO = {
  ABIERTO: { etiqueta: 'Sin decidir', color: 'azul' },
  CONFIRMADO: { etiqueta: 'Se va a reclamar', color: 'ambar' },
  COBRADO: { etiqueta: 'Cobrado', color: 'verde' },
  DESCARTADO: { etiqueta: 'Se decidió no reclamar', color: 'gris' },
  // NUNCA UN COLOR QUE CONSUELA. Un cargo que venció sin que nadie lo
  // mirara es dinero perdido, y en rojo es donde se ve.
  CADUCADO: { etiqueta: 'Se perdió por no reclamar', color: 'rojo' },
};

// Los cuatro números, en el orden en que cuentan la historia: lo que se
// puede pedir, lo que se pidió, lo que entró, y lo que se dejó morir.
const COLUMNAS = [
  { clave: 'reclamable', titulo: 'Se puede reclamar', clase: 'text-gray-900' },
  { clave: 'reclamado', titulo: 'Ya se reclamó', clase: 'text-gray-900' },
  { clave: 'cobrado', titulo: 'Cobrado', clase: 'text-green-700' },
  { clave: 'caduco_sin_reclamar', titulo: 'Se perdió por no reclamar', clase: 'text-red-700' },
];

// EL CUARTO DETECTOR · VEN-6. Va en su propia fila porque es dinero en la
// otra dirección: no lo que el proveedor nos debe, sino el descuento que
// NOSOTROS perdemos si pagamos tarde. Sumarlo con «se puede reclamar»
// mentiría en las dos cuentas.
const PRONTO_PAGO = [
  { clave: 'por_aprovechar', titulo: 'Descuento por pagar pronto, aún a tiempo', clase: 'text-gray-900' },
  { clave: 'aprovechado', titulo: 'Descuento aprovechado', clase: 'text-green-700' },
  { clave: 'perdido_pronto_pago', titulo: 'Descuento perdido por pagar tarde', clase: 'text-red-700' },
];

// =====================================================================
// CUANDO SE MIRO · VEN-8
// =====================================================================
// VA ENCIMA DE LAS CUATRO CIFRAS Y NO DEBAJO, porque es lo que dice si
// hay que creérselas. Un cero ahí significaba dos cosas opuestas —«se
// miró y no había nada» y «nadie ha mirado»— y la pantalla no las
// distinguía. Es la Regla 5 del encargo, «nunca un icono verde que
// miente», entrando por la puerta de atrás: lo que mentía era un cero, y
// de un icono verde la gente desconfía; de un cero, no.
//
// La regla vive en `ventaja/ultimaRevision.js`, sin React, para poder
// probarla sola. Aquí solo se pinta.
function UltimaRevision({ revision }) {
  const e = estadoDeLaRevision(revision);
  const amarillo = e.color === 'amarillo';
  return (
    <div
      // `alert` solo cuando hay algo que atender: un lector de pantalla
      // que anuncia «revisado hoy a las 3:40» cada vez que se abre la
      // pantalla acaba siendo ruido que la gente aprende a saltarse.
      role={amarillo ? 'alert' : undefined}
      className={`rounded-lg border p-2.5 ${
        amarillo
          ? 'border-amber-200 bg-amber-50'
          : 'border-gray-200 bg-gray-50'}`}
    >
      <div className="flex items-start gap-2">
        <Clock className={`h-4 w-4 shrink-0 mt-0.5 ${
          amarillo ? 'text-amber-700' : 'text-gray-400'}`} aria-hidden="true" />
        <div>
          <span className={`block text-sm font-semibold ${
            amarillo ? 'text-amber-900' : 'text-gray-700'}`}>
            {e.titulo}
            {e.cuando ? ` ${cuandoEnPalabras(e.cuando)}` : ''}
          </span>
          <span className={`block text-xs mt-0.5 ${
            amarillo ? 'text-amber-800' : 'text-gray-500'}`}>
            {e.detalle}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Una cifra del tablero, con lo que todavía no se sabe dicho aparte. */
export function Cifra({ titulo, dato, clase = '' }) {
  const sin = Number(dato?.sin_importe || 0);
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <span className="block text-xs font-semibold text-gray-600">{titulo}</span>
      <span className={`block text-xl font-bold tabular-nums mt-0.5 ${clase}`}>
        {dinero(dato?.importe)}
      </span>
      <span className="block text-xs text-gray-500 mt-0.5">
        {Number(dato?.cuantos || 0)} caso(s)
        {/* «TODAVÍA NO SE SABE» NO ES CERO PESOS. Sumarlo como cero haría
            que poner el parámetro que falta pareciera un aumento salido
            de la nada. */}
        {sin > 0 && (
          <span className="block text-amber-700 font-semibold">
            {sin} sin importe todavía
          </span>
        )}
      </span>
    </div>
  );
}

export default function DineroEnLaMesa() {
  // EL PERMISO SE LEE AQUI Y NO LLEGA COMO PROPIEDAD: `AppShell` monta
  // cada pantalla con `irA` y `contexto`, y nada mas. Recibirlo por
  // propiedad lo dejaria siempre en `false` — que es justo el defecto que
  // tenia la pantalla de parametros comerciales y que se arreglo con
  // esto mismo.
  const { puede } = useAuth();
  const puedeDecidir = puede('cargos_a_proveedor');
  const [tablero, setTablero] = useState(null);
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [decidir, setDecidir] = useState(null);
  const [enBloque, setEnBloque] = useState(false);
  const [revisando, setRevisando] = useState(false);
  const avisos = useAvisos();

  const cargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const [t, l] = await Promise.all([
        api.get('/cargos-proveedor/tablero'),
        api.get('/cargos-proveedor'),
      ]);
      setTablero(t);
      setLista(l);
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message
        : 'No se pudo cargar. Vuelve a intentarlo en un momento.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  async function revisarAhora() {
    setRevisando(true);
    try {
      const r = await api.post('/cargos-proveedor/revisar', {});
      await cargar();
      // SE DICE QUÉ SALIÓ, INCLUIDO EL CERO. «Listo» sin número deja a
      // quien lo pulsó sin saber si es que no hay nada o si no funcionó.
      avisos.exito(
        r.propuestas > 0
          ? `${r.propuestas} caso(s) nuevo(s) por revisar.`
          : 'Revisado: no hay ningún incumplimiento nuevo.');
    } catch (e) {
      avisos.aviso(e instanceof ErrorApi ? e.message
        : 'No se pudo revisar. Vuelve a intentarlo en un momento.');
    } finally {
      setRevisando(false);
    }
  }

  const columnas = [
    {
      etiqueta: 'Qué pasó', valor: (c) => TIPO[c.tipo]?.etiqueta || c.tipo,
      render: (c) => <ChipEstado mapa={TIPO} clave={c.tipo} />,
    },
    { etiqueta: 'Proveedor', valor: (c) => c.proveedor, clase: 'font-medium text-gray-900' },
    {
      etiqueta: 'Cuánto', alineado: 'derecha', valor: (c) => Number(c.cantidad),
      render: (c) => (
        <span className="tabular-nums">
          {Number(c.cantidad)} {c.unidad === 'DIAS' ? 'día(s)' : 'pieza(s)'}
        </span>
      ),
    },
    {
      etiqueta: 'Importe', alineado: 'derecha', valor: (c) => Number(c.importe || 0),
      render: (c) => (c.importe !== null && c.importe !== undefined
        ? <span className="tabular-nums font-semibold">{dinero(c.importe)}</span>
        // SE DICE POR QUÉ FALTA, no se pinta un guion. Un hueco en una
        // columna de dinero se lee como cero, y cero es una afirmación.
        : (
          <span className="text-xs text-amber-800">
            Falta un dato para calcularlo
          </span>
        )),
    },
    {
      etiqueta: 'Evidencia', alineado: 'derecha', valor: (c) => c.fotos || 0,
      render: (c) => (Number(c.fotos) > 0 ? (
        <span className="inline-flex items-center gap-1 text-xs text-gray-700">
          <Camera className="h-3.5 w-3.5" aria-hidden="true" />
          {c.fotos} foto(s)
        </span>
      ) : <span className="text-xs text-gray-600">sin fotos</span>),
    },
    {
      etiqueta: 'Caduca', valor: (c) => c.caduca_el || '',
      render: (c) => (c.caduca_el
        ? <span className="tabular-nums text-xs">{c.caduca_el}</span>
        : (
          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
            <Clock className="h-3.5 w-3.5" aria-hidden="true" />
            sin plazo puesto
          </span>
        )),
    },
    {
      etiqueta: 'Estado', valor: (c) => ESTADO[c.estado]?.etiqueta || c.estado,
      render: (c) => <ChipEstado mapa={ESTADO} clave={c.estado} />,
    },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Coins className="h-5 w-5 text-[var(--color-marca)]" aria-hidden="true" />
          Descuentos de proveedor sin cobrar
        </h2>
        <Ayuda id="cargos.pantalla" />
      </div>

      <Ayuda id="cargos.no_cobra" />

      {tablero && <UltimaRevision revision={tablero.ultima_revision} />}

      {tablero && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {COLUMNAS.map((c) => (
            <Cifra key={c.clave} titulo={c.titulo} dato={tablero[c.clave]}
                   clase={c.clase} />
          ))}
        </div>
      )}

      {tablero?.por_aprovechar && (
        <section aria-label="Descuentos por pronto pago">
          <Ayuda id="cargos.pronto_pago" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
            {PRONTO_PAGO.map((c) => (
              <Cifra key={c.clave} titulo={c.titulo} dato={tablero[c.clave]}
                     clase={c.clase} />
            ))}
          </div>
          {puedeDecidir && <ProntoPagoDeProveedor onGuardado={cargar} />}
        </section>
      )}

      {tablero?.metodo && (
        // EL MÉTODO VIAJA CON EL NÚMERO · Fase 3.2. Un tablero de dinero
        // sin la definición de lo que suma es una opinión con formato de
        // dato.
        <p className="text-xs text-gray-500">
          {tablero.metodo.nombre} · versión {tablero.metodo.version}{' '}
          ({tablero.metodo.fecha}).
        </p>
      )}

      {error && (
        <p role="alert" className="text-sm text-red-700">{error}</p>
      )}

      {puedeDecidir && (
        <div className="flex flex-wrap gap-2">
          {lista.some((c) => c.estado === 'ABIERTO') && (
            <button
              type="button" onClick={() => setEnBloque(true)}
              className="text-sm font-semibold px-3 py-2 rounded-lg border border-[var(--color-marca)] text-[var(--color-marca)] hover:bg-[var(--color-marca-suave)]"
            >
              Reclamar varios de una vez
            </button>
          )}
          {/* LA REVISIÓN A MANO EXISTE PARA EL PRIMER DÍA. Alguien acaba
              de poner sus parámetros y quiere ver qué sale sin esperar a
              la madrugada. Es idempotente: pulsarla dos veces no propone
              dos veces. */}
          <button
            type="button" onClick={revisarAhora} disabled={revisando}
            className="text-sm font-semibold px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {revisando ? 'Revisando…' : 'Revisar ahora'}
          </button>
        </div>
      )}

      {!cargando && lista.length === 0 ? (
        <EstadoVacio
          icono={Coins}
          titulo="Todavía no hay nada que reclamar"
          descripcion={
            'La revisión de cada noche busca entregas tarde, material que '
            + 'faltó y lotes rechazados. Si esta lista está vacía, o los '
            + 'proveedores están cumpliendo, o todavía no hay órdenes '
            + 'cerradas con las que comparar.'
          }
          accion={puedeDecidir ? (
            <button
              type="button" onClick={revisarAhora} disabled={revisando}
              className="text-sm font-semibold px-4 py-2 rounded-lg bg-[var(--color-marca)] text-white disabled:opacity-50"
            >
              {revisando ? 'Revisando…' : 'Revisar ahora'}
            </button>
          ) : undefined}
        />
      ) : (
        <ListaEstandar
          columnas={columnas}
          filas={lista}
          cargando={cargando}
          claveFila={(c) => c.id}
          acciones={puedeDecidir ? (c) => (
            c.estado === 'ABIERTO'
              ? [{ etiqueta: 'Voy a reclamarlo', onClick: () => setDecidir({ cargo: c, decision: 'CONFIRMADO' }) },
                 { etiqueta: 'No reclamarlo', onClick: () => setDecidir({ cargo: c, decision: 'DESCARTADO' }) }]
              : c.estado === 'CONFIRMADO'
                ? [{ etiqueta: 'Ya me lo abonaron', onClick: () => setDecidir({ cargo: c, decision: 'COBRADO' }) }]
                : []
          ) : undefined}
        />
      )}

      {enBloque && (
        <ModalEnBloque
          abiertos={lista.filter((c) => c.estado === 'ABIERTO')}
          onCerrar={() => setEnBloque(false)}
          onListo={(n) => {
            setEnBloque(false); cargar();
            avisos.exito(`${n} cargo(s) anotados para reclamar.`);
          }}
        />
      )}

      {decidir && (
        <ModalDecidir
          cargo={decidir.cargo}
          decision={decidir.decision}
          onCerrar={() => setDecidir(null)}
          onListo={() => { setDecidir(null); cargar(); avisos.exito('Queda anotado.'); }}
        />
      )}
    </div>
  );
}

// EXPORTADO PARA PODERLO PROBAR, igual que `CamposDeCalidad`: es lo que
// existe para DECIR qué va a pasar antes de que pase.
export function ModalDecidir({ cargo, decision, onCerrar, onListo }) {
  const [motivo, setMotivo] = useState('');
  const esDescartar = decision === 'DESCARTADO';

  const titulo = {
    CONFIRMADO: 'Reclamar este cargo',
    DESCARTADO: 'No reclamar este cargo',
    COBRADO: 'Marcarlo como cobrado',
  }[decision];

  return (
    <ModalConfirmar
      titulo={titulo}
      descripcion={
        decision === 'CONFIRMADO'
          // LO QUE HACE Y LO QUE NO, en la misma frase. Sin esto, alguien
          // pulsa «reclamar» esperando que salga un documento.
          ? 'Queda anotado que vas a reclamarlo, con tu nombre y la fecha. '
            + 'TEGO no emite ninguna nota de cargo ni le manda nada al '
            + 'proveedor: el documento lo haces tú con tu contador.'
          : esDescartar
            ? 'El cargo no desaparece: queda con tu motivo, para poder '
              + 'revisarlo después.'
            : 'Queda anotado que este dinero ya entró.'
      }
      cambios={[
        { campo: 'Proveedor', antes: '', despues: cargo.proveedor },
        {
          campo: 'Importe',
          antes: '',
          despues: (cargo.importe !== null && cargo.importe !== undefined)
            ? dinero(cargo.importe)
            : 'sin calcular todavía',
        },
      ]}
      extra={esDescartar ? (
        <label className="block">
          <span className="block text-sm font-semibold text-gray-700 mb-1">
            Por qué no se reclama
          </span>
          <textarea
            value={motivo} rows={3} maxLength={240}
            onChange={(e) => setMotivo(e.target.value)}
            aria-label="Motivo para no reclamar"
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
          />
          <Ayuda id="cargos.motivo_descartar" />
        </label>
      ) : undefined}
      etiquetaConfirmar={titulo}
      onConfirmar={async () => {
        if (esDescartar && motivo.trim().length < 3) {
          throw new ErrorApi(
            'Escribe por qué no se reclama. Decidir no cobrarle a un '
            + 'proveedor es justo la decisión que hay que poder revisar '
            + 'después, y sin el motivo no se puede.');
        }
        await api.post(`/cargos-proveedor/${cargo.id}/decidir`,
                       { decision, motivo: motivo.trim() || undefined });
        onListo();
      }}
      onCerrar={onCerrar}
    />
  );
}


// =====================================================================
// CONFIRMAR VARIOS DE UNA VEZ · Fase 5.2
// =====================================================================
//
// LAS DOS REGLAS SON DEL DISEÑO QUE YA ESTABA PROBADO en los cargos
// mensuales de la consola, y ninguna es decorativa:
//
//   · **El importe total se teclea.** Es la segunda llave, y aquí
//     protege más que un nombre: un «¿seguro?» se pulsa sin mirar; un
//     total, no. Se compara contra lo que suman los seleccionados.
//   · **Se asientan todos o ninguno.** Media tanda decidida es peor que
//     ninguna, porque nadie sabría cuáles. Lo garantiza el servidor, en
//     una sola transacción.
//
// LOS CARGOS SIN IMPORTE SE PUEDEN SELECCIONAR y no suman al total. Se
// dice en pantalla: si no, alguien teclea el total que ve y el servidor
// contesta que no cuadra sin que se entienda por qué.
export function ModalEnBloque({ abiertos, onCerrar, onListo }) {
  const [elegidos, setElegidos] = useState(() => abiertos.map((c) => c.id));
  const [total, setTotal] = useState('');

  const seleccionados = abiertos.filter((c) => elegidos.includes(c.id));
  const suma = seleccionados.reduce((s, c) => s + Number(c.importe || 0), 0);
  const sinImporte = seleccionados.filter(
    (c) => c.importe === null || c.importe === undefined).length;

  return (
    <ModalConfirmar
      titulo="Reclamar varios cargos"
      descripcion={
        'Queda anotado que vas a reclamarlos, con tu nombre y la fecha. '
        + 'TEGO no emite ninguna nota de cargo ni le manda nada a ningún '
        + 'proveedor.'
      }
      extra={
        <div className="space-y-3">
          <ul className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-y-auto">
            {abiertos.map((c) => (
              <li key={c.id} className="px-3 py-2">
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox" className="mt-1"
                    checked={elegidos.includes(c.id)}
                    aria-label={`Incluir el cargo de ${c.proveedor}`}
                    onChange={(e) => setElegidos(
                      (v) => (e.target.checked
                        ? [...v, c.id]
                        : v.filter((x) => x !== c.id)))}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-gray-900">
                      {c.proveedor} · {TIPO[c.tipo]?.etiqueta || c.tipo}
                    </span>
                    <span className="block text-xs text-gray-500">
                      {(c.importe !== null && c.importe !== undefined)
                        ? dinero(c.importe)
                        : 'sin importe: no suma al total'}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>

          <div className="rounded-lg border border-gray-200 p-3">
            <span className="block text-sm text-gray-700">
              Los {seleccionados.length} seleccionados suman{' '}
              <strong className="tabular-nums">{dinero(suma)}</strong>.
            </span>
            {sinImporte > 0 && (
              <span className="block text-xs text-amber-800 mt-0.5">
                {sinImporte} de ellos no tienen importe y no
                suman nada a ese total.
              </span>
            )}
            <label className="block mt-2">
              <span className="block text-xs font-semibold text-gray-700 mb-1">
                Teclea ese total para confirmar
              </span>
              <input
                value={total} inputMode="decimal"
                aria-label="Importe total"
                onChange={(e) => setTotal(e.target.value)}
                className="w-40 px-2 py-1.5 border border-gray-300 rounded text-sm text-right tabular-nums"
              />
              <Ayuda id="cargos.total_en_bloque" />
            </label>
          </div>
        </div>
      }
      etiquetaConfirmar={`Reclamar ${seleccionados.length}`}
      onConfirmar={async () => {
        if (!seleccionados.length) {
          throw new ErrorApi('No seleccionaste ningún cargo.');
        }
        const r = await api.post('/cargos-proveedor/decidir-en-bloque', {
          ids: seleccionados.map((c) => c.id),
          decision: 'CONFIRMADO',
          importe_total: total.trim() || '0',
        });
        onListo(r.decididos);
      }}
      onCerrar={onCerrar}
    />
  );
}

// =====================================================================
// LAS CONDICIONES DE PRONTO PAGO · VEN-6
// =====================================================================
// Viven aquí porque el dato alimenta a este detector y quien decide
// reclamar es quien las captura. Un formulario de tres campos: el
// proveedor, el porcentaje y los días. Vaciar los dos números lo quita.
export function ProntoPagoDeProveedor({ onGuardado }) {
  const [proveedores, setProveedores] = useState([]);
  const [idProveedor, setIdProveedor] = useState('');
  const [pct, setPct] = useState('');
  const [dias, setDias] = useState('');
  const [guardando, setGuardando] = useState(false);
  const avisos = useAvisos();

  useEffect(() => {
    api.get('/compras/proveedores').then(setProveedores).catch(() => setProveedores([]));
  }, []);

  const aMedias = (pct === '') !== (dias === '');
  const listo = idProveedor !== '' && !aMedias && !guardando;

  async function guardar(e) {
    e.preventDefault();
    if (!listo) return;
    setGuardando(true);
    try {
      const r = await api.put(`/cargos-proveedor/proveedores/${idProveedor}/pronto-pago`, {
        pct: pct === '' ? null : pct,
        dias: dias === '' ? null : Number(dias),
      });
      avisos.exito(r.mensaje);
      onGuardado?.();
    } catch (err) {
      avisos.error(err instanceof ErrorApi ? err.message
        : 'No se pudo guardar. Revisa el porcentaje y los días e inténtalo otra vez.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form onSubmit={guardar}
          className="mt-3 rounded-xl border border-gray-200 bg-white p-3 grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
      <label className="block text-sm sm:col-span-2">
        <span className="block text-xs font-semibold text-gray-600">Proveedor</span>
        <select value={idProveedor} onChange={(e) => setIdProveedor(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 min-h-[44px]">
          <option value="">Elige un proveedor</option>
          {proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
        </select>
      </label>
      <label className="block text-sm">
        <span className="block text-xs font-semibold text-gray-600">Descuento (%)</span>
        <input type="number" inputMode="decimal" min="0.01" max="99.99" step="0.01" value={pct}
               onChange={(e) => setPct(e.target.value)} placeholder="Ej. 2"
               className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 min-h-[44px]" />
      </label>
      <label className="block text-sm">
        <span className="block text-xs font-semibold text-gray-600">Si se paga antes de (días)</span>
        <input type="number" inputMode="numeric" min="1" max="120" step="1" value={dias}
               onChange={(e) => setDias(e.target.value)} placeholder="Ej. 10"
               className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 min-h-[44px]" />
      </label>
      <div className="sm:col-span-4 flex flex-wrap items-center gap-2">
        <button type="submit" disabled={!listo}
                className="rounded-lg bg-blue-600 text-white px-4 py-2 min-h-[44px] font-semibold disabled:opacity-50">
          {guardando ? 'Guardando…' : 'Guardar condiciones'}
        </button>
        {aMedias && (
          <span className="text-xs text-amber-700">
            Lleva las dos cosas: el porcentaje y los días. Para quitar el descuento, deja los dos vacíos.
          </span>
        )}
        {idProveedor === '' && !aMedias && (
          <span className="text-xs text-gray-500">Elige el proveedor para poder guardar.</span>
        )}
      </div>
    </form>
  );
}
